import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Building2, Server, User, Copyright, Mail } from 'lucide-react';
import SEO from '@/components/SEO';
import { COMPANY_INFO } from '@/lib/constants';

const MentionsLegales = () => {
    return (
        <>
            <SEO 
                title="Mentions Légales"
                description="Mentions légales d'Orcsys. Informations sur l'éditeur du site, l'hébergement, et les conditions d'utilisation."
                keywords="mentions légales, conditions d'utilisation, hébergement, éditeur, droits d'auteur"
                type="legal"
            />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white pt-24">
                <div className="container px-4 mx-auto py-12">
                    <div className="max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12"
                        >
                            <div className="flex items-center gap-3 mb-8">
                                <Shield className="w-8 h-8 text-orcsys-500" />
                                <h1 className="text-4xl font-bold text-slate-900">
                                    Mentions Légales
                                </h1>
                            </div>

                            <div className="prose prose-slate max-w-none">
                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Building2 className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Informations légales
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Le site orcsys.fr est édité par Orcsys, une société innovante spécialisée dans le développement de solutions numériques.
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6 mb-4">
                                            <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                <li className="flex items-center gap-2">
                                                    <span className="font-semibold min-w-[140px]">Raison sociale :</span>
                                                    <span>Orcsys</span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <span className="font-semibold min-w-[140px]">Forme juridique :</span>
                                                    <span>Société à responsabilité limitée (SARL)</span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <span className="font-semibold min-w-[140px]">SIRET :</span>
                                                    <span>{COMPANY_INFO.siret}</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Server className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Hébergement
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            L'hébergement du site est assuré par Infomaniak, leader dans l'hébergement de solutions web modernes.
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                <li className="flex items-center gap-2">
                                                    <span className="font-semibold min-w-[140px]">Hébergeur :</span>
                                                    <span>Infomaniak Network SA</span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <span className="font-semibold min-w-[140px]">Adresse :</span>
                                                    <span>Rue Eugène Marziano 25, 1227 Les Acacias, Suisse</span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <span className="font-semibold min-w-[140px]">Site web :</span>
                                                    <a href="https://infomaniak.com" target="_blank" rel="noopener noreferrer" className="text-orcsys-500 hover:text-orcsys-600">
                                                        infomaniak.com
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Copyright className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Propriété intellectuelle
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            L'ensemble du contenu de ce site (architecture, textes, photos, illustrations, logos, icônes, fichiers disponibles en téléchargement, etc.) est la propriété exclusive d'Orcsys et est protégé par le droit d'auteur et le droit des marques.
                                        </p>
                                        <p className="text-slate-600 mb-4">
                                            Toute reproduction, représentation, modification, publication, transmission, dénaturation, totale ou partielle du site ou de son contenu, par quelque procédé que ce soit, et sur quelque support que ce soit est interdite sans l'autorisation écrite préalable d'Orcsys.
                                        </p>
                                        <p className="text-slate-600">
                                            Toute exploitation non autorisée du site ou de son contenu engagerait la responsabilité de l'utilisateur et constituerait une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la Propriété Intellectuelle.
                                        </p>
                                    </div>
                                </section>

                                <section>
                                    <div className="flex items-center gap-3 mb-4">
                                        <Mail className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Contact
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Pour toute question concernant ces mentions légales ou pour signaler un contenu ou un comportement illicite, vous pouvez nous contacter :
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                <li className="flex items-center gap-2">
                                                    <span className="font-semibold min-w-[140px]">Email :</span>
                                                    <a href="mailto:contact@orcsys.fr" className="text-orcsys-500 hover:text-orcsys-600">
                                                        {COMPANY_INFO.email}
                                                    </a>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <span className="font-semibold min-w-[140px]">Téléphone :</span>
                                                    <span>{COMPANY_INFO.phone}</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MentionsLegales; 